import { connect } from 'react-redux';

import { IKnmCheckSubjectResult } from 'APIPackage/KnmModule/interfaces/IKnmCheckSubjectResult';
import { KnmInnField } from 'KnmPackage/MainModule/components/KnmInnField';
import { KnmOgrnField } from 'KnmPackage/MainModule/components/KnmOgrnField';
import { IKnmErknmFormSubject } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormSubject';
import { useKnmSetFnsLookupLoadingFlags } from 'KnmPackage/MainModule/hooks/useKnmSetFnsLookupLoadingFlags';
import { TextareaField } from 'UIPackage/MainModule/components/TextareaField';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { Row } from 'UIPackage/FormModule/components/Row';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { parsePassport } from 'CorePackage/MainModule/common/parsePassport';
import { IInspectionResult } from 'APIPackage/CatalogsModule/interfaces/IInspectionResult';
import { RafpRequestStatusTypeEnum } from 'APIPackage/KnmModule/enums/RafpRequestStatusTypeEnum';
import { ITargetEntity } from 'APIPackage/DictionariesModule/interfaces/ITargetEntity';
import { IDictionarySelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { ICountry } from 'APIPackage/CatalogsModule/interfaces/ICountry';
import { InspectionResultFieldEnum } from 'APIPackage/CatalogsModule/enums/InspectionResultFieldEnum';
import { KnmSnilsField } from 'KnmPackage/MainModule/components/KnmSnilsField';
import { composeValidators } from 'CorePackage/MainModule/validators/composeValidators';
import { makeRequired } from 'CorePackage/MainModule/validators/isRequired';
import { MAX_CHARACTERS_ERROR, REQUIRED_ERROR } from 'CorePackage/MainModule/constants/validationErrors';
import { makeMaxCharactersValidator } from 'CorePackage/MainModule/validators/maxCharacters';
import { SelectInputFieldAutoOptions } from 'UIPackage/MainModule/components/SelectInputFieldAutoOptions';
import { CommonCatalogsNamesEnum } from 'CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { getDecisionTargetEntitySelectOptions } from 'DecisionPackage/MainModule/selectors/decisionSelectors';
import { KnmSubjectTypesEnum } from 'KnmErknmPackage/ErknmSubjectModule/components/ErknmSubjectInfo';
import { getTitleFieldWithRedStar } from 'functions/getTitleFieldWithRedStar';
import { Hint } from 'CorePackage/MainModule/components/Hint';

import styles from './DecisionActSubjectInfo.module.scss';

interface IDecisionActSubjectInfoProps {
  ervkId: string | undefined;
  checkSubjectResult: IKnmCheckSubjectResult | null;
  inn: string | undefined;
  inspectionResults?: IInspectionResult[];
  isAutocomplete: IKnmErknmFormSubject['isAutocomplete'];
  kpp: string | undefined;
  name: string | undefined;
  ogrn: string | undefined;
  rafpNza: string | undefined;
  smevRequestStatus: RafpRequestStatusTypeEnum | undefined;
  snils: string | undefined;
  subjectType: IDictionarySelectOption<ITargetEntity> | undefined;
  isDecision: boolean | undefined;
}

interface IDecisionActSubjectInfoStoreProps {
  targetEntitySelectOptions: IDictionarySelectOption<ITargetEntity>[] | undefined;
}

const DecisionActSubjectInfoWithoutStore = ({
  ervkId,
  checkSubjectResult,
  inn,
  inspectionResults,
  isAutocomplete,
  kpp,
  name,
  ogrn,
  rafpNza,
  smevRequestStatus,
  snils,
  subjectType,
  targetEntitySelectOptions,
  isDecision,
}: IDecisionActSubjectInfoProps & IDecisionActSubjectInfoStoreProps) => {
  const isFIZ = subjectType?.label === KnmSubjectTypesEnum.FIZ;
  const isIFIZ = subjectType?.label === KnmSubjectTypesEnum.IFIZ;
  const isFIUR = subjectType?.label === KnmSubjectTypesEnum.FIUR;

  const { setFnsLookupByOgrnLoadingFlag, setFnsLookupByInnLoadingFlag } = useKnmSetFnsLookupLoadingFlags();

  const ogrnFieldName = `${name}.ogrn`;
  const innFieldName = `${name}.inn`;
  const organizationNameFieldName = `${name}.originalName`;
  const subjectTypeFieldName = `${name}.organizationType`;
  const kppFieldName = `${name}.kpp`;
  const rafpNzaFieldName = `${name}.rafpNza`;
  const countryFieldName = `${name}.country`;
  const locationAddressFieldName = `${name}.locationAddress`;
  const workAddressFieldName = `${name}.workAddress`;
  const factualAddressFieldName = `${name}.factualAddress`;
  const snilsFieldName = `${name}.snils`;
  const documentFieldName = `${name}.document`;

  const isRafpNzaSent = smevRequestStatus === RafpRequestStatusTypeEnum.REQUEST_SENT;
  const isRafpNzaError = smevRequestStatus === RafpRequestStatusTypeEnum.ERROR_RETRY;

  const isOrganizationNameFieldEnabledWithoutFiz = false;

  const isOrganizationNameFieldEnabled = isFIZ && isOrganizationNameFieldEnabledWithoutFiz;

  const getOrganizationNameFieldRequired = () => !(isOrganizationNameFieldEnabledWithoutFiz && isFIZ);

  const normalizeFio = (value: string | undefined) => {
    if (!value) return value;

    return value.replace(/[^а-яёa-z-`' ]/gi, '');
  };

  const isRafpFilled = !!rafpNza && /^\d{11}$/.test(rafpNza);

  const saveKnmAndWaitFromRafpWarning =
    (isRafpFilled || (inn && kpp)) && !smevRequestStatus
      ? 'Сохраните КНМ и ожидайте загрузки информации из РАФП'
      : undefined;

  const waitFromRafpWarning = isRafpNzaSent ? 'Ожидайте загрузки информации из РАФП' : undefined;

  const rafpWarning = isFIUR ? saveKnmAndWaitFromRafpWarning || waitFromRafpWarning : undefined;

  const innOrSnilsWarning = isFIZ && !inn && !snils ? 'Необходимо заполнить либо ИНН, либо СНИЛС' : undefined;
  const filterUlCountries = (values: ICountry[]) => values?.filter(item => item.ulEnabled);
  const filterFlCountries = (values: ICountry[]) => values?.filter(item => item.flEnabled);

  const IROrganizationName = inspectionResults?.find(
    i => i.inspectedField === InspectionResultFieldEnum.ORGANIZATION_FNS_NAME,
  );
  const IRLocationAddress = inspectionResults?.find(
    i => i.inspectedField === InspectionResultFieldEnum.ORGANIZATION_FNS_ADDRESS,
  );
  const IRFactualAddress = inspectionResults?.find(
    i => i.inspectedField === InspectionResultFieldEnum.ORGANIZATION_FNS_FACTUAL_ADDRESS,
  );
  const IRRafpNza = inspectionResults?.find(i => i.inspectedField === InspectionResultFieldEnum.RAFP_REGISTRY);

  return (
    <>
      <Row>
        <FieldBlock
          label={'Тип контролируемого лица'}
          placementLabel="left"
          color="black"
          className={styles.FieldBlock}
        >
          <SelectInputField
            id={subjectTypeFieldName}
            name={subjectTypeFieldName}
            options={targetEntitySelectOptions}
            disabled={true}
            size="large"
          />
        </FieldBlock>
      </Row>

      {subjectType && (
        <>
          {((subjectType.label !== KnmSubjectTypesEnum.IUR && !isIFIZ) ||
            subjectType.label === KnmSubjectTypesEnum.FIUR ||
            isFIZ) && (
            <Row>
              {subjectType.label !== KnmSubjectTypesEnum.IUR && !isIFIZ && (
                <FieldBlock
                  label={getTitleFieldWithRedStar({
                    label: `Идентификационный номер налогоплательщика (ИНН)`,
                    field: `innFieldName`,
                    isPm: false,
                    isDecision,
                    isKnm: false,
                  })}
                  placementLabel="left"
                  color="black"
                  className={styles.FieldBlock}
                  id={'organization.act.organization.innLabelBlock'}
                >
                  <KnmInnField
                    name={innFieldName}
                    disabled={true}
                    inn={inn}
                    isFiz={isFIZ}
                    isShowAutoCompleteField={
                      subjectType.label !== KnmSubjectTypesEnum.IUR && subjectType.label !== KnmSubjectTypesEnum.FIUR
                    }
                    onLoadingChange={setFnsLookupByInnLoadingFlag}
                    ervkId={ervkId}
                    subjectType={subjectType}
                    checkSubjectResult={checkSubjectResult}
                    isShowAdditionalMessages={true}
                  />
                </FieldBlock>
              )}
              {subjectType.label === KnmSubjectTypesEnum.FIUR && (
                <FieldBlock
                  label={getTitleFieldWithRedStar({
                    label: `Номер записи об аккредитации в РАФП`,
                    field: `${rafpNzaFieldName}`,
                    isPm: false,
                    isDecision,
                    isKnm: false,
                  })}
                  placementLabel="left"
                  color="black"
                  className={styles.FieldBlock}
                  id={'organization.act.organization.rafpNzaLabelBlock'}
                >
                  <div className={styles.ColumnWrapper}>
                    <TextInputField
                      name={rafpNzaFieldName}
                      disabled={true}
                      warning={rafpWarning}
                      inspectionResult={IRRafpNza}
                    />
                    {isRafpNzaError && (
                      <Hint
                        text="Запрашиваемые сведения не могут быть предоставлены в настоящий момент. Автоматический запрос
                        будет произведен через час"
                      />
                    )}
                  </div>
                </FieldBlock>
              )}
              {isFIZ && (
                <FieldBlock
                  label={getTitleFieldWithRedStar({
                    label: `Страховой номер индивидуального лицевого счета (СНИЛС)`,
                    field: `${snilsFieldName}`,
                    isPm: false,
                    isDecision,
                    isKnm: false,
                  })}
                  placementLabel="left"
                  color="black"
                  className={styles.FieldBlock}
                >
                  <KnmSnilsField name={snilsFieldName} warning={innOrSnilsWarning} disabled={true} />
                </FieldBlock>
              )}
            </Row>
          )}
          {(subjectType.label === KnmSubjectTypesEnum.UR || subjectType.label === KnmSubjectTypesEnum.IP) && (
            <Row>
              <FieldBlock
                label="Основной государственный регистрационный номер (ОГРН)"
                placementLabel="left"
                color="black"
                className={styles.FieldBlock}
                id={'organization.act.organization.ogrnLabelBlock'}
              >
                <KnmOgrnField
                  name={ogrnFieldName}
                  disabled={true}
                  ogrn={ogrn}
                  onLoadingChange={setFnsLookupByOgrnLoadingFlag}
                />
              </FieldBlock>
            </Row>
          )}
          {subjectType.label === KnmSubjectTypesEnum.FIUR && (
            <Row>
              <FieldBlock
                label={getTitleFieldWithRedStar({
                  label: `Код причины постановки на учет (КПП)`,
                  field: `${kppFieldName}`,
                  isPm: false,
                  isDecision,
                  isKnm: false,
                })}
                id={'organization.act.organization.kppLabelBlock'}
                placementLabel="left"
                color="black"
                className={styles.FieldBlock}
              >
                <TextInputField name={kppFieldName} disabled={true} warning={rafpWarning} />
              </FieldBlock>
            </Row>
          )}
          {(subjectType.label === KnmSubjectTypesEnum.UR ||
            subjectType.label === KnmSubjectTypesEnum.IP ||
            subjectType.label === KnmSubjectTypesEnum.FIUR) && (
            <Row>
              <FieldBlock
                label={getTitleFieldWithRedStar({
                  label: `Наименование проверяемого лица`,
                  field: `${organizationNameFieldName}`,
                  isPm: false,
                  isDecision,
                  isKnm: false,
                })}
                id={'organization.act.organization.organizationNameBlock'}
                placementLabel="left"
                color="black"
                className={styles.FieldBlock}
              >
                <TextareaField
                  id={'organization.act.organization.organizationNameBlock'}
                  inputClassName={styles.OrganizationNameInput}
                  name={organizationNameFieldName}
                  resize="none"
                  clearable={isOrganizationNameFieldEnabled && isAutocomplete}
                  disabled={true}
                  inspectionResult={IROrganizationName}
                  validate={composeValidators(
                    makeRequired(REQUIRED_ERROR, getOrganizationNameFieldRequired),
                    makeMaxCharactersValidator(1024, MAX_CHARACTERS_ERROR),
                  )}
                />
              </FieldBlock>
            </Row>
          )}
          {(isIFIZ || subjectType.label === KnmSubjectTypesEnum.FIZ) && (
            <Row>
              <FieldBlock
                id={'organization.act.organization.organizationNameBlock'}
                label={getTitleFieldWithRedStar({
                  label: `Фамилия, имя, отчество (при наличии)`,
                  field: `${organizationNameFieldName}`,
                  isPm: false,
                  isDecision,
                  isKnm: false,
                })}
                placementLabel="left"
                color="black"
                className={styles.FieldBlock}
              >
                <TextInputField
                  name={organizationNameFieldName}
                  disabled={true}
                  id={'organization.act.organization.organizationNameBlock'}
                  parse={normalizeFio}
                />
              </FieldBlock>
            </Row>
          )}
          {(subjectType.label === KnmSubjectTypesEnum.UR || subjectType.label === KnmSubjectTypesEnum.FIUR) && (
            <Row>
              <FieldBlock
                label="Адрес места нахождения"
                placementLabel="left"
                color="black"
                className={styles.FieldBlock}
              >
                <TextInputField name={locationAddressFieldName} disabled={true} inspectionResult={IRLocationAddress} />
              </FieldBlock>
            </Row>
          )}
          {subjectType.label === KnmSubjectTypesEnum.UR && (
            <Row>
              <FieldBlock
                label="Место нахождения (осуществления деятельности) контролируемого лица"
                placementLabel="left"
                color="black"
                className={styles.FieldBlock}
              >
                <TextInputField name={workAddressFieldName} disabled={true} />
              </FieldBlock>
            </Row>
          )}
          {subjectType.label === KnmSubjectTypesEnum.IP && (
            <Row>
              <FieldBlock
                label="Место фактического осуществления деятельности"
                placementLabel="left"
                color="black"
                className={styles.FieldBlock}
              >
                <TextInputField name={factualAddressFieldName} disabled={true} inspectionResult={IRFactualAddress} />
              </FieldBlock>
            </Row>
          )}
          {isIFIZ && (
            <Row>
              <FieldBlock
                label={getTitleFieldWithRedStar({
                  label: `Сведения о документе, удостоверяющем личность`,
                  field: `${documentFieldName}`,
                  isPm: false,
                  isDecision,
                  isKnm: false,
                })}
                placementLabel="left"
                color="black"
                className={styles.FieldBlock}
              >
                <TextInputField name={documentFieldName} parse={parsePassport} disabled={true} />
              </FieldBlock>
            </Row>
          )}
          {subjectType.label === KnmSubjectTypesEnum.IUR && (
            <Row>
              <FieldBlock
                label={getTitleFieldWithRedStar({
                  label: `Наименование`,
                  field: `${organizationNameFieldName}`,
                  isPm: false,
                  isDecision,
                  isKnm: false,
                })}
                id={'organization.act.organization.organizationNameBlock'}
                placementLabel="left"
                color="black"
                className={styles.FieldBlock}
              >
                <TextInputField
                  id={'organization.act.organization.organizationNameBlock'}
                  name={organizationNameFieldName}
                  validate={composeValidators(
                    makeRequired(REQUIRED_ERROR, getOrganizationNameFieldRequired),
                    makeMaxCharactersValidator(1024, MAX_CHARACTERS_ERROR),
                  )}
                  disabled={true}
                />
              </FieldBlock>
            </Row>
          )}
          {(subjectType.label === KnmSubjectTypesEnum.IUR || isIFIZ) && (
            <Row>
              <FieldBlock
                label={getTitleFieldWithRedStar({
                  label: `${
                    subjectType.label === KnmSubjectTypesEnum.IUR
                      ? 'Государство, в котором указанное юридическое лицо зарегистрировано'
                      : 'Гражданство (при наличии)'
                  }`,
                  field: `${countryFieldName}`,
                  isPm: false,
                  isDecision,
                  isKnm: false,
                })}
                placementLabel="left"
                color="black"
                className={styles.FieldBlock}
              >
                <SelectInputFieldAutoOptions
                  id={countryFieldName}
                  name={countryFieldName}
                  isClearable={false}
                  size="medium"
                  optionName={CommonCatalogsNamesEnum.COUNTRIES}
                  disabled={true}
                  transformFunc={isIFIZ ? filterFlCountries : filterUlCountries}
                />
              </FieldBlock>
            </Row>
          )}
        </>
      )}
    </>
  );
};

const mapStateToProps = (state: IRootState): IDecisionActSubjectInfoStoreProps => ({
  targetEntitySelectOptions: getDecisionTargetEntitySelectOptions(state),
});

export const DecisionActSubjectInfo = connect(mapStateToProps)(DecisionActSubjectInfoWithoutStore);
